import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    updateEnableBuybox,
    updateEnableDayparting,
    updateEnableRevstack,
    updateEnableKeywordRecommendation,
} from "@features/adminSettingsSlice";
import ToggleSwitch from "@buttons/ToggleSwitch";
import {
    Features,
    IAccess,
    IClientAccess,
    IAdminToggleBtn,
    Platforms,
    IReportsData,
    ParentModule,
    SubModule,
    TActiveClientId,
    TAdminSettings,
    TUserdata,
    TReportsData,
    AdminSettings,
} from "@ts/common-types";
import { updateShowAdminSettingsDisplay, updateConfirmModalDisplay } from "@features/modalSlice";
import { showToast } from "@ts/modals";
import { getClientReports, updateAdminSettings } from "@ts/retrieve-data";
import UserSettingsModal from "./UserSettingsModal";
import { ReactComponent as SettingsIcon } from "@images/settings_icon.svg";
import { ReactComponent as UserSettingsIcon } from "@images/user_gear_solid.svg";
import * as Sentry from "@sentry/react";
import ReportsSettingsModal from "./ReportsSettingsModal";
import { useNavigate } from "react-router-dom";
import { getActiveModule } from "@ts/utils";
import store from "@/store";
import usePermissionCheck from "@/hooks/usePermissionCheck";
import useGetModulesData from "@/hooks/useGetModulesData";

interface IAdminSettings {
    showAdminSettings: boolean;
}

const AdminSettingsModal: React.FC<IAdminSettings> = ({
    showAdminSettings,
}) => {
    const { activeModule } = getActiveModule();
    const navigate = useNavigate();
    const enableDayparting = useSelector(
        (state: IAdminToggleBtn) => state.enableDayparting.value
    );
    const enableBuybox = useSelector(
        (state: IAdminToggleBtn) => state.enableBuybox.value
    );
    const enableRevstack = useSelector(
        (state: IAdminToggleBtn) => state.enableRevstack.value
    );
    const enableKeywordRecommendation = useSelector(
        (state: IAdminToggleBtn) => state.enableKeywordRecommendation.value
    );
    const [dayPartingStatus, setDayPartingStatus] =
        useState<boolean>(enableDayparting);
    const [buyboxStatus, setBuyboxStatus] = useState<boolean>(enableBuybox);
    const [revstackStatus, setRevstackStatus] =
        useState<boolean>(enableRevstack);
    const [keywordRecommendationStatus, setKeywordRecommendationStatus] =
        useState<boolean>(enableKeywordRecommendation);
    const [addUsersDisplay, setAddUsersDisplay] = useState(false);
    const [addReportsDisplay, setAddReportsDisplay] = useState(false);
    const [existingReportsData, setExistingReportsData] =
        useState<Array<IReportsData>>();
    const [getUpdatedReports, setGetUpdatedReports] = useState<boolean>(false);
    const dispatch = useDispatch();
    const activeClientId: number = useSelector(
        (state: TActiveClientId) => +state.activeClientId.value
    );

    const userData = useSelector((state: TUserdata) => state.userData.value);

    const userAccess = userData?.access?.find((access: IAccess) =>
        access.clients.find(
            (client: IClientAccess) => client.client_id === activeClientId
        )
    );

    const platformAccess = userAccess
        ? userAccess?.platforms.map((item: string) => item.toLowerCase())
        : [];

    const userHasActivationAccess = userData?.is_super_admin || platformAccess.includes(
        Platforms.ACTIVATION
    );

    const userHasPlanningAccess = userData?.is_super_admin || platformAccess.includes(Platforms.PLANNING);

    const { canListReports } = usePermissionCheck();

    const platformFeatures = useGetModulesData({ type: "platform-features" });

    const reportsData = useSelector(
        (state: TReportsData) => state.reportsData.value
    );

    const isFirstRender = useRef(true);

    useEffect(() => {
        const fetchReports = async () => {
            try {
                if (!canListReports) {
                    setExistingReportsData([]);
                } else if (reportsData && reportsData.revstack && reportsData.common) {
                    setExistingReportsData([
                        ...reportsData.revstack,
                        ...reportsData.common,
                    ]);
                } else {
                    setExistingReportsData([]);
                }
            } catch (err) {
                showToast("danger", "Unable to get reports data");
            }
        };

        fetchReports();
    }, [enableRevstack, canListReports, activeClientId, reportsData]);


    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        const fetchClientReports = async () => {
            await store.dispatch(getClientReports());
        };

        fetchClientReports();
    }, [getUpdatedReports]);

    useEffect(() => {
        resetToDefaultSettings();
    }, [
        enableDayparting,
        enableBuybox,
        enableRevstack,
        enableKeywordRecommendation,
    ]);

    useEffect(() => {
        return () => {
            dispatch(updateShowAdminSettingsDisplay(false));
            resetToDefaultSettings();
        };
    }, []);

    const resetToDefaultSettings = () => {
        setDayPartingStatus(enableDayparting);
        setBuyboxStatus(enableBuybox);
        setRevstackStatus(enableRevstack);
        setKeywordRecommendationStatus(enableKeywordRecommendation);
    };

    const closeAdminSettingsModal = () => {
        dispatch(updateShowAdminSettingsDisplay(false));
        resetToDefaultSettings();
    };

    const onToggleChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const componentId = e.target.id;
        switch (componentId) {
            case "toggle-dpr-settings":
                setDayPartingStatus((prev) => !prev);
                break;

            case "toggle-buybox-settings":
                setBuyboxStatus((prev) => !prev);
                break;

            case "toggle-revstack-settings":
                setRevstackStatus((prev) => !prev);
                break;
            case "toggle-keyword-recommendation-settings":
                setKeywordRecommendationStatus((prev) => !prev);
                break;
        }
    };

    const performSave = async (updatedSettings: TAdminSettings) => {
        try {
            const updatedSettingsKeys = Object.keys(updatedSettings);
            if (updatedSettingsKeys.length !== 0) {
                const response = await updateAdminSettings(updatedSettings);
                if (!response) {
                    throw new Error(response.message);
                }
                updatedSettingsKeys.includes(AdminSettings.REVSTACK) &&
                    !updatedSettings.revstack &&
                    activeModule === ParentModule.REVSTACK &&
                    navigate(`/${ParentModule.PERFORMANCE_OVERVIEW}`);
                updatedSettingsKeys.includes(AdminSettings.KEYWORD_RECOMMENDATION) &&
                    !updatedSettings[AdminSettings.KEYWORD_RECOMMENDATION] &&
                    [
                        SubModule.KEYWORD_AUTO,
                        SubModule.KEYWORD_MANUAL,
                        SubModule.KEYWORD_REVINITI,
                    ].includes(activeModule as SubModule) &&
                    navigate(`/${ParentModule.PERFORMANCE_OVERVIEW}`);
                dispatch(updateEnableDayparting(dayPartingStatus));
                dispatch(updateEnableBuybox(buyboxStatus));
                dispatch(updateEnableRevstack(revstackStatus));
                dispatch(
                    updateEnableKeywordRecommendation(
                        keywordRecommendationStatus
                    )
                );
                showToast(
                    `${response.status ? "success" : "danger"}`,
                    response.message
                );
            }
        } catch (err) {
            console.error(err);
            Sentry.captureException(err);
        }
    };

    const onSaveClickHandler = async () => {
        const updatedSettings: TAdminSettings = {};

        if (buyboxStatus !== enableBuybox) {
            updatedSettings[AdminSettings.BUYBOX] = buyboxStatus;
        }

        if (dayPartingStatus !== enableDayparting) {
            updatedSettings[AdminSettings.DAY_PARTING] = dayPartingStatus;
        }

        if (revstackStatus !== enableRevstack) {
            updatedSettings[AdminSettings.REVSTACK] = revstackStatus;
        }

        if (keywordRecommendationStatus !== enableKeywordRecommendation) {
            updatedSettings[AdminSettings.KEYWORD_RECOMMENDATION] =
                keywordRecommendationStatus;
        }

        const updatedSettingsKeys = Object.keys(updatedSettings);
        if (updatedSettingsKeys.length === 0) return;

        if (
            Object.prototype.hasOwnProperty.call(
                updatedSettings,
                AdminSettings.DAY_PARTING
            ) &&
            !updatedSettings[AdminSettings.DAY_PARTING]
        ) {
            dispatch(
                updateConfirmModalDisplay({
                    value: true,
                    title: "Disable Dayparting",
                    message:
                        "This will disable dayparting across all campaigns! All of the current campaign schedules will be saved. To use dayparting again in the future, it'll have to be manually re-enabled for the desired campaigns. Are you sure you want to continue?",
                    onConfirm: async () => {
                        await performSave(updatedSettings);
                    },
                })
            );
            return;
        }

        await performSave(updatedSettings);
    };

    return (
        <>
            <div>
                <div className="card bg-light admin-settings-container">
                    <div
                        className={`admin-settings-components ${platformFeatures.includes(Features.DAY_PARTING) &&
                            userHasActivationAccess
                                ? ""
                                : "d-none"
                            }`}
                    >
                        <label>Enable / Disable Day Parting</label>
                        <ToggleSwitch
                            id="toggle-dpr-settings"
                            onToggleChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                            ) => onToggleChangeHandler(e)}
                            size="0.8rem"
                            checked={dayPartingStatus}
                        />
                    </div>

                    <div
                        className={`admin-settings-components ${platformFeatures.includes(Features.BUYBOX) &&
                            userHasActivationAccess
                                ? ""
                                : "d-none"
                            }`}
                    >
                        <label> Enable / Disable Buybox</label>
                        <ToggleSwitch
                            id="toggle-buybox-settings"
                            onToggleChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                            ) => onToggleChangeHandler(e)}
                            size="0.8rem"
                            checked={buyboxStatus}
                        />
                    </div>

                    <div
                        className={`admin-settings-components ${platformFeatures.includes(Features.REVSTACK) &&
                            userHasPlanningAccess
                                ? ""
                                : "d-none"
                            }`}
                    >
                        <label> Enable / Disable Custom Reports</label>
                        <ToggleSwitch
                            id="toggle-revstack-settings"
                            onToggleChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                            ) => onToggleChangeHandler(e)}
                            size="0.8rem"
                            checked={revstackStatus}
                        />
                    </div>

                    <div
                        className={`admin-settings-components ${platformFeatures.includes(
                            Features.KEYWORD_RECOMMENDATIONS
                        ) && userHasActivationAccess
                                ? ""
                                : "d-none"
                            }`}
                    >
                        <label> Enable / Disable Keyword Recommendation</label>
                        <ToggleSwitch
                            id="toggle-keyword-recommendation-settings"
                            onToggleChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                            ) => onToggleChangeHandler(e)}
                            size="0.8rem"
                            checked={keywordRecommendationStatus}
                        />
                    </div>

                    {/* add report and add user*/}
                    <>
                        <div className="admin-settings-components">
                            <label>Reports Configuration</label>
                        </div>
                        {/* add user */}
                        <div
                            className="reset-admin-settings ms-3"
                            style={{ marginTop: "-5px" }}
                        >
                            <div className="admin-settings-components ms-3">
                                <div>
                                    <label className="admin-settings-subcomponent">
                                        {" "}
                                        User Settings
                                    </label>
                                </div>
                                <UserSettingsIcon
                                    className="me-2"
                                    role="button"
                                    fill="#5c5e63"
                                    height="20"
                                    width="20"
                                    onClick={() => setAddUsersDisplay(true)}
                                />
                            </div>
                        </div>
                        {/* add reports */}

                        <div
                            className="reset-admin-settings ms-3"
                            style={{ marginTop: "-7px" }}
                        >
                            <div className="admin-settings-components ms-3">
                                <div>
                                    <label className="admin-settings-subcomponent">
                                        {" "}
                                        Reports Settings
                                    </label>
                                </div>
                                <SettingsIcon
                                    className="me-2"
                                    role="button"
                                    fill="#5c5e63"
                                    height="20"
                                    width="20"
                                    onClick={() => setAddReportsDisplay(true)}
                                />
                            </div>
                        </div>
                    </>

                    <div className="reset-admin-settings">
                        <div className="admin-settings-components">
                            <div>
                                <label className="d-block">
                                    {" "}
                                    Reset Settings
                                </label>
                                <label className="d-block text-secondary reset-admin-settings-label">
                                    Restore settings to their original defaults
                                </label>
                            </div>
                            <button
                                className="btn btn-sm"
                                style={{ backgroundColor: "#D8D8D8" }}
                                onClick={resetToDefaultSettings}
                            >
                                Reset
                            </button>
                        </div>
                    </div>
                </div>
                <div className="mt-3 float-end">
                    <button
                        className="btn btn-sm btn-secondary me-2"
                        onClick={closeAdminSettingsModal}
                    >
                        Cancel
                    </button>
                    <button
                        id="save-settings-btn"
                        className="btn btn-sm btn-primary"
                        onClick={onSaveClickHandler}
                        disabled={
                            dayPartingStatus === enableDayparting &&
                            buyboxStatus === enableBuybox &&
                            revstackStatus === enableRevstack &&
                            keywordRecommendationStatus ===
                            enableKeywordRecommendation
                        }
                    >
                        Save Changes
                    </button>
                </div>
            </div>
            <UserSettingsModal {...{ addUsersDisplay, setAddUsersDisplay }} />
            <ReportsSettingsModal
                {...{
                    addReportsDisplay,
                    setAddReportsDisplay,
                    existingReportsData,
                    setGetUpdatedReports,
                }}
            />
        </>
    );
};

export default AdminSettingsModal;
